import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex, Sticky, VFlex } from '@/components/base';
import ScrollView from '@/components/Common/ScrollView';
import Typo from '@/components/Typography/Typo';
import SentimentFeedbackItem from '@/features/Feedback/field/Sentiment/SentimentFeedbackItem';
import useColor from '@/hooks/domain/useColor';
import useIntersectionObserver from '@/hooks/pure/useIntersectionObserver';
import useFeedbackNavigationStore from '@/stores/Feedback/FeedbackNavigationStore';
import { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import { scrollToTargetAdjusted } from '@/utils/dom';

type Props = {
  alias: string;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
  data: LinkedFeedbackResponse;
};

const PAGE_SIZE = 50;

const FeedbackTab = (props: Props) => {
  const scrollElementId = `content_detail_modal_scroll_selector_${props.alias}`;
  const color = useColor();
  const loaderRef = useRef(null);
  const [page, setPage] = useState(1);

  const [selectedFeedback, setSelectedFeedback] = useState<
    number | string | null
  >(null);

  const onIntersect = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      entry.isIntersecting && setPage((prev) => prev + 1);
    });
  };

  const { setToPrevFeedback, setToNextFeedback } = useFeedbackNavigationStore(
    (state) => ({
      setToPrevFeedback: state.setToPrevFeedback,
      setToNextFeedback: state.setToNextFeedback,
    }),
  );

  let delayTimer = useRef<NodeJS.Timer | null>(null);
  const delay = 200;

  const toPrevFeedback = () => {
    const currIndex = list?.current.findIndex(
      (f) => f.alias === selectedFeedback,
    );
    let index: number;
    if (currIndex === -1) return;
    if (currIndex === 0) index = 0;
    index = currIndex - 1;
    if (list?.current[index]?.alias) {
      if (delayTimer.current) {
        clearTimeout(delayTimer.current);
      }
      delayTimer.current = setTimeout(() => {
        setSelectedFeedback(list?.current[index]?.alias);
        props.onSelectNewModal(
          list?.current[index]?.alias,
          DETAIL_MODAL_TYPE.FEEDBACK,
        );
        const target = document.querySelector(
          `[data-feedback-id="${list?.current[index]?.alias}"]`,
        );
        const scrollTarget = document.querySelector(`#${scrollElementId}`);
        if (target && scrollTarget) {
          scrollToTargetAdjusted(target, scrollTarget, delay);
        }
      }, delay);
    }
  };

  const toNextFeedback = () => {
    const currIndex = list?.current.findIndex(
      (f) => f.alias === selectedFeedback,
    );
    let index: number;
    if (currIndex === -1) return;
    if (currIndex === list?.current.length - 1)
      index = list?.current.length - 1;
    index = currIndex + 1;
    if (list?.current[index]?.alias) {
      if (delayTimer.current) {
        clearTimeout(delayTimer.current);
      }
      delayTimer.current = setTimeout(() => {
        setSelectedFeedback(list?.current[index]?.alias);
        props.onSelectNewModal(
          list?.current[index]?.alias,
          DETAIL_MODAL_TYPE.FEEDBACK,
        );
        const target = document.querySelector(
          `[data-feedback-id="${list?.current[index]?.alias}"]`,
        );
        const scrollTarget = document.querySelector(`#${scrollElementId}`);
        if (target && scrollTarget) {
          scrollToTargetAdjusted(target, scrollTarget, delay);
        }
      }, delay);
    }
  };

  const list = useMemo(() => {
    const totalLen = props.data?.feedbacks.length || 0;

    return {
      current: props.data?.feedbacks.slice(0, PAGE_SIZE * page) || [],
      total: totalLen,
      hasNextPage: PAGE_SIZE * page <= totalLen,
    };
  }, [page, props.data?.feedbacks]);

  useEffect(() => {
    setToPrevFeedback(toPrevFeedback);
    setToNextFeedback(toNextFeedback);
    return () => {
      setToPrevFeedback(null);
      setToNextFeedback(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, setToNextFeedback, setToPrevFeedback, selectedFeedback]);

  useIntersectionObserver({
    target: loaderRef,
    threshold: 0.1,
    onIntersect,
  });

  return (
    <ScrollView
      style={{
        height: 'calc(100vh - 242px)',
        padding: '0px 32px 32px',
      }}
      id={scrollElementId}
    >
      <Style>
        <Sticky
          style={{
            background: color.gray100,
            padding: '16px 0 16px',
            zIndex: 1,
            boxShadow: `0px 6px 8px ${color.gray100}`,
          }}
        >
          <Flex j="space-between" a="flex-end" style={{ minHeight: 32 }}>
            <Flex a="center">
              <Typo type="label2" color={color.gray700}>
                Total feedback: {list.total}
              </Typo>
            </Flex>
          </Flex>
        </Sticky>

        <VFlex g={8}>
          {list?.current.map((f) => (
            <SentimentFeedbackItem
              key={f.alias}
              index={f.alias}
              sentiment={f.sentiment}
              focused={selectedFeedback === f.alias}
              text={f.title || 'Untitled'}
              tid={f.tid}
              account_name={f.company_info?.name}
              contact_name={f.customer_info?.name}
              onClick={() => {
                setSelectedFeedback(f.alias);
                props.onSelectNewModal(f.alias, DETAIL_MODAL_TYPE.FEEDBACK);
              }}
            />
          ))}
          {list.hasNextPage && (
            <Flex ref={loaderRef} style={{ height: 60 }}></Flex>
          )}
          {list.total === 0 && (
            <CenterFlex style={{ height: 300 }}>No feedback</CenterFlex>
          )}
        </VFlex>
      </Style>
    </ScrollView>
  );
};

const Style = styled(VFlex)`
  gap: 8px;
  padding-bottom: 24px;
`;

export default FeedbackTab;
